/**
 * Core 2048 Game Types
 *
 * All TypeScript types and interfaces for the 2048 sliding tile puzzle.
 * Board: 4×4 grid (configurable). Single-player puzzle.
 */

// ============================================================================
// Board Configuration
// ============================================================================

/** Valid tile values (powers of 2) */
export type TileValue = number;

/** A single tile on the board */
export interface Tile {
  /** Unique identifier for animation tracking */
  id: number;
  /** Current value (2, 4, 8, 16, ...) */
  value: number;
  /** Row position (0-indexed) */
  row: number;
  /** Column position (0-indexed) */
  col: number;
  /** Whether this tile was created from a merge this turn */
  mergedFrom?: boolean;
  /** Whether this tile is newly spawned */
  isNew?: boolean;
  /** Previous row (for slide animation) */
  previousRow?: number;
  /** Previous col (for slide animation) */
  previousCol?: number;
}

/** 4×4 board represented as a 2D array of Tile or null */
export type Board2048 = (Tile | null)[][];

/** Slide direction */
export type Direction = 'up' | 'down' | 'left' | 'right';

// ============================================================================
// Game State
// ============================================================================

export interface Game2048State {
  /** Current board state */
  board: Board2048;
  /** Current score */
  score: number;
  /** Best score ever achieved */
  bestScore: number;
  /** Game status */
  gameStatus: 'idle' | 'playing' | 'won' | 'game-over';
  /** Whether the player chose to continue after winning */
  keepPlaying: boolean;
  /** Move history for undo */
  moveHistory: Game2048Snapshot[];
  /** All active tiles (flat list for rendering) */
  tiles: Tile[];
  /** Board size (default 4) */
  boardSize: number;
  /** Unique game identifier */
  gameId: string;
  /** Timestamp when the game started */
  startedAt: number;
  /** Total number of moves made */
  moveCount: number;
}

/** Snapshot for undo support */
export interface Game2048Snapshot {
  board: Board2048;
  score: number;
  tiles: Tile[];
  gameStatus: 'idle' | 'playing' | 'won' | 'game-over';
}

// ============================================================================
// Game Settings
// ============================================================================

export interface Game2048Settings {
  /** Board size (default 4) */
  boardSize?: number;
}

// ============================================================================
// Statistics
// ============================================================================

export interface Game2048Statistics {
  userId?: string;
  gamesPlayed: number;
  bestScore: number;
  highestTile: number;
  totalScore: number;
  gamesWon: number;
  winPercentage: number;
  averageScore: number;
  totalMoves: number;
}

export interface Game2048GameResult {
  gameId: string;
  score: number;
  highestTile: number;
  won: boolean;
  moveCount: number;
  completedAt: number;
  boardSize: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface Game2048Props {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface Game2048ModeSelectorProps {
  onStartGame: (settings: Game2048Settings) => void;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
}

export interface Board2048Props {
  board: Board2048;
  tiles: Tile[];
  boardSize: number;
  disabled?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

export const DEFAULT_BOARD_SIZE = 4;
export const WIN_TILE_VALUE = 2048;

/** Tile background colors by value */
export const TILE_COLORS: Record<number, string> = {
  2: '#eee4da',
  4: '#ede0c8',
  8: '#f2b179',
  16: '#f59563',
  32: '#f67c5f',
  64: '#f65e3b',
  128: '#edcf72',
  256: '#edcc61',
  512: '#edc850',
  1024: '#edc53f',
  2048: '#edc22e',
};

/** Tile text colors: dark for 2/4, white for 8+ */
export const TILE_TEXT_COLORS: Record<number, string> = {
  2: '#776e65',
  4: '#776e65',
};

export const DEFAULT_TILE_TEXT_COLOR = '#f9f6f2';
